import { useEffect, useState, useRef, useCallback } from 'react'
import { TypePopup, useUpsellStore } from '@/store/upsell'

interface UseUpsellCountdownOptions {
  /**
   * Loại popup (PAYMENT hoặc UPSELL)
   */
  type: TypePopup
  /**
   * Thời gian hết hạn từ API (ISO string hoặc timestamp)
   */
  expireDate?: string | null
  /**
   * Callback được gọi khi countdown kết thúc
   */
  onComplete?: () => void
}

/**
 * Custom hook để quản lý countdown cho upsell popups
 * Tự động sync giữa các tabs thông qua Zustand store
 * Xử lý browser throttling khi tab không focus
 */
export const useUpsellCountdown = ({ type, expireDate, onComplete }: UseUpsellCountdownOptions) => {
  const { expireAt, setExpireAt } = useUpsellStore()
  const [expireAfter, setExpireAfter] = useState(0)
  
  // Sử dụng ref để tránh re-create interval khi onComplete thay đổi
  const onCompleteRef = useRef(onComplete)
  const hasExpiredRef = useRef(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Cập nhật ref khi onComplete thay đổi (không trigger useEffect)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  // Lưu expireAt từ API vào store để đồng bộ giữa các tabs
  useEffect(() => {
    if (expireDate) {
      const expiry = new Date(expireDate).getTime()
      setExpireAt(expiry)
      hasExpiredRef.current = false // Reset expired flag khi có expire date mới
    }
  }, [expireDate, setExpireAt])

  // Function tính toán thời gian còn lại
  const calculateRemaining = useCallback(() => {
    if (!expireAt) {
      setExpireAfter(0)
      return 0
    }

    const now = Date.now()
    const diffInSeconds = Math.floor((expireAt - now) / 1000)
    const remaining = diffInSeconds > 0 ? diffInSeconds : 0
    
    setExpireAfter(remaining)
    
    // Gọi onComplete một lần duy nhất khi countdown kết thúc
    if (remaining === 0 && !hasExpiredRef.current) {
      hasExpiredRef.current = true
      console.log('⏰ [useUpsellCountdown] Countdown expired, calling onComplete')
      onCompleteRef.current?.()
    }

    return remaining
  }, [expireAt])

  // Tính toán countdown mỗi giây
  useEffect(() => {
    // Clear interval cũ nếu có (tránh multiple intervals)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    // Tính toán ngay lập tức
    const remaining = calculateRemaining()
    
    // Nếu đã hết hạn, không cần tạo interval
    if (remaining <= 0) {
      return
    }

    // Tạo interval mới
    intervalRef.current = setInterval(() => {
      const current = calculateRemaining()
      
      // Dừng interval nếu countdown đã hết
      if (current <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }, 1000)

    // Cleanup: Clear interval khi component unmount hoặc dependencies thay đổi
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [calculateRemaining])

  // Xử lý khi tab được focus (browser throttling)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Force tính toán lại ngay lập tức
        const remaining = calculateRemaining()
        // Nếu đã hết hạn mà chưa gọi callback (do browser throttling)
        if (remaining === 0 && !hasExpiredRef.current) {
          hasExpiredRef.current = true
          onCompleteRef.current?.()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [calculateRemaining])

  return { expireAfter }
}
